import React from 'react';
import './Stickman.css';

interface StickmanProps {
  position: number;
  isMoving: boolean;
  hasPartyHat?: boolean;
}

const Stickman: React.FC<StickmanProps> = ({ position, isMoving, hasPartyHat = false }) => {
  const getPositionOnPath = (x: number) => {
    const progress = x / 800;
    const y = 350 - (progress * 200);
    const xPos = x + 50;
    return { 
      xPercent: (xPos / 900) * 100, 
      yPercent: (y / 400) * 100 
    };
  };

  const { xPercent, yPercent } = getPositionOnPath(position);

  return (
    <div 
      className="stickman"
      style={{ 
        left: `${xPercent}%`, 
        top: `${yPercent}%`,
        transform: `translate(-50%, -50%)`
      }}
    >
      <svg
        width="60"
        height="80"
        viewBox="0 -8 48 72"
        className="stickman-svg"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Whole body subtle push motion */}
        <g>
          {isMoving && (
            <animateTransform
              attributeName="transform"
              type="translate"
              dur="0.6s"
              repeatCount="indefinite"
              keyTimes="0;0.5;1"
              values="0 0;1 0;0 0"
            />
          )}

          {/* Slight rocking */}
          <g>
            {isMoving && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                dur="0.6s"
                repeatCount="indefinite"
                keyTimes="0;0.5;1"
                values="-4 24 28;4 24 28;-4 24 28"
              />
            )}

            {/* Head */}
            <circle
              cx="24"
              cy="10"
              r="5"
              stroke="#2c3e50"
              strokeWidth="2"
              fill="#FFDBAC"
            />

            {/* Party Hat - shown when happiness is level 10 */}
            {hasPartyHat && (
              <g className="party-hat">
                <path
                  d="M 18 4 L 24 -7 L 30 4 L 28 6 L 20 6 Z"
                  fill="#FF6B9D"
                  stroke="#C44569"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <circle cx="24" cy="-4" r="2.5" fill="#FFD93D" />
                <circle cx="20" cy="5" r="1.8" fill="#FFD93D" />
                <circle cx="28" cy="5" r="1.8" fill="#FFD93D" />
              </g>
            )}

            {/* Torso */}
            <line
              x1="24"
              y1="15"
              x2="28"
              y2="30"
              stroke="#2c3e50"
              strokeWidth="2"
              strokeLinecap="round"
            />

            {/* ARMS grown from shoulders */}
            {/* Left shoulder: (22,18), Right shoulder: (26,18) */}
            <g>
              {/* Left arm (pushing) */}
              <line
                x1="22"
                y1="18"
                x2="34"
                y2="20"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Left forearm/hand */}
              <line
                x1="34"
                y1="20"
                x2="40"
                y2="20"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Left hand */}
              <circle
                cx="40"
                cy="20"
                r="2"
                fill="#FFDBAC"
              />

              {/* Right arm (swinging) */}
              <line
                x1="26"
                y1="18"
                x2="34"
                y2="24"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Right forearm/hand */}
              <line
                x1="34"
                y1="24"
                x2="40"
                y2="24"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Right hand */}
              <circle
                cx="40"
                cy="24"
                r="2"
                fill="#FFDBAC"
              />

              {/* Arm push motion */}
              {isMoving && (
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  dur="0.6s"
                  repeatCount="indefinite"
                  keyTimes="0;0.5;1"
                  values="0 0;1 0;0 0"
                />
              )}
            </g>

            {/* LEGS with alternating motion */}
            <g>
              {/* Left leg */}
              <line
                x1="28"
                y1="30"
                x2="22"
                y2="48"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              >
                {isMoving && (
                  <animate
                    attributeName="x2"
                    dur="0.6s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="22;34;22"
                  />
                )}
              </line>
              {/* Left foot */}
              <circle
                cx="22"
                cy="48"
                r="2"
                fill="#2c3e50"
              >
                {isMoving && (
                  <animate
                    attributeName="cx"
                    dur="0.6s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="22;34;22"
                  />
                )}
              </circle>

              {/* Right leg */}
              <line
                x1="28"
                y1="30"
                x2="34"
                y2="48"
                stroke="#2c3e50"
                strokeWidth="2"
                strokeLinecap="round"
              >
                {isMoving && (
                  <animate
                    attributeName="x2"
                    dur="0.6s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="34;22;34"
                  />
                )}
              </line>
              {/* Right foot */}
              <circle
                cx="34"
                cy="48"
                r="2"
                fill="#2c3e50"
              >
                {isMoving && (
                  <animate
                    attributeName="cx"
                    dur="0.6s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="34;22;34"
                  />
                )}
              </circle>
            </g>

            {/* Effort lines when pushing */}
            {isMoving && (
              <>
                <line
                  x1="38"
                  y1="18"
                  x2="44"
                  y2="14"
                  stroke="#FF6B6B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="3,3"
                >
                  <animate
                    attributeName="opacity"
                    dur="0.3s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="0;1;0"
                  />
                </line>
                <line
                  x1="38"
                  y1="20"
                  x2="44"
                  y2="20"
                  stroke="#FF6B6B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="3,3"
                >
                  <animate
                    attributeName="opacity"
                    dur="0.3s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="0;1;0"
                    begin="0.1s"
                  />
                </line>
                <line
                  x1="38"
                  y1="22"
                  x2="44"
                  y2="26"
                  stroke="#FF6B6B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="3,3"
                >
                  <animate
                    attributeName="opacity"
                    dur="0.3s"
                    repeatCount="indefinite"
                    keyTimes="0;0.5;1"
                    values="0;1;0"
                    begin="0.2s"
                  />
                </line>
              </>
            )}
          </g>
        </g>
      </svg>
    </div>
  );
};

export default Stickman;
